import { BNGoogleTagManager, EventAction } from "../../Helpers/GoogleTagManager/BNGoogleTagManager";
import { useRef } from "react";

import { NavigationLink } from "../BNHeader/BNHeader";
import { useLocation, useNavigate } from "react-router";
import getRouteAction from "../../Helpers/dataUitAttributes/dataUitAttributes";
import isMenuItemSelected from "../../Helpers/isMenuItemSelected/isMenuItemSelected";
import { Paper } from "@mui/material";
import BNButton from "../FormElements/BNButton/BNButton";
import BNBox from "../BNBox/BNBox";
import { SxProps, Theme, useTheme } from "@mui/material/styles";
import { UiElementClickEvent } from "../../Helpers/GoogleTagManager/UiElementClickEvent";

interface Props {
  links: Array<NavigationLink | undefined>;
  isSpa: (path: string) => boolean;
  isAppLook: boolean;
  sx?: SxProps<Theme>;
}

export const BNTopNavigation: React.FC<Props> = (props) => {
  const { links: linksProp, isSpa = () => false, isAppLook, sx } = props;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const mobileNavigationButtonWrapperRef = useRef<HTMLDivElement>(null);

  const links = linksProp.filter((x) => !!x);

  const menuItemClicked = (link: NavigationLink) => {
    BNGoogleTagManager.addEventToDataLayer(new UiElementClickEvent(EventAction.TopMobileNavigation, link.title));
    if (isSpa(link.url)) {
      void navigate(link.url);
    } else {
      window?.location?.replace(link.url);
    }
  };

  const justOneMenuItemAndForCurrentPage = links?.length === 1 && isMenuItemSelected(pathname, links[0].url, true);
  if (justOneMenuItemAndForCurrentPage) {
    return null;
  }

  return (
    <Paper
      data-uit-container="top-navigation"
      sx={{
        display: "flex",
        position: "relative",
        justifyContent: "center",
        flexWrap: "nowrap",
        overflow: "hidden",
        borderRadius: 0,
        m: 0,
        padding: `2px 2px`,
        maxWidth: "100%",
        backgroundColor: isAppLook ? "transparent" : undefined,
        ...theme.applyStyles("dark", {
          backgroundColor: "transparent",
        }),
        minHeight: "4.1rem",
        [theme.breakpoints.up("md")]: {
          minHeight: "4.8rem",
        },
        "@media print": {
          display: "none",
        },
        ...sx,
      }}
      ref={mobileNavigationButtonWrapperRef}
    >
      <BNBox
        component={"nav"}
        sx={{
          overflowY: "hidden",
          overflowX: "auto",
          scrollbarWidth: "thin",
          whiteSpace: "nowrap",
          mx: 2,
        }}
      >
        {links?.map((link, index) => (
          <MobileNavigationButton
            links={links}
            link={link}
            index={index}
            key={`mobile-top-nav-${index}`}
            currentPath={pathname}
            clickHandler={menuItemClicked}
            isApp={isAppLook}
          />
        ))}
      </BNBox>
    </Paper>
  );
};

interface MobileNavigationButtonProps {
  links: NavigationLink[];
  link: NavigationLink;
  index: number;
  currentPath: string;
  clickHandler: (link: NavigationLink) => void;
  isApp: boolean;
}
const MobileNavigationButton: React.FC<MobileNavigationButtonProps> = (props) => {
  const theme = useTheme();
  const { index, clickHandler, isApp } = props;
  const isSelected = isMenuItemSelected(props.currentPath, props.link.url, index === 0);

  return (
    <BNButton
      href={props.link.url}
      data-uit={getRouteAction(props.link.url)}
      onClick={(e) => {
        e.preventDefault();
        clickHandler(props.link);
      }}
      sx={{
        overflow: "clip",
        fontWeight: "normal",
        fontSize: { xs: "1.2rem !important", md: "1.6rem !important" },
        whiteSpace: "nowrap",
        paddingLeft: { xs: 1, md: 3 },
        paddingRight: { xs: 1, md: 3 },
        ["&.MuiButton-contained"]: {
          backgroundColor: theme.vars.palette.primary.light,
          [":hover"]: {
            backgroundColor: theme.vars.palette.primary.main,
          },
        },
      }}
      size="medium"
      variant={isApp ? "text" : isSelected ? "contained" : "text"}
      textDecoration={isApp ? isSelected : false}
    >
      {props.link.title}
    </BNButton>
  );
};
